import type {ScriptBlockData} from '../components/story/ScriptBlock';

export interface ParagraphGeometry {x:number;width:number;before:number;align:'left'|'right'|'center'}
export interface ResolvedParagraphFormat extends ParagraphGeometry {firstIndent:number;startsNewPage:boolean;lineHeight:number;warnings:string[]}
export interface ScriptFormatSource {
    page:Readonly<Record<string,string>>;
    elements:Readonly<Record<string,Readonly<Record<string,string>>>>;
    paginateAs?:Readonly<Record<string,string>>;
    fonts?:Readonly<Record<string,Readonly<Record<string,string>>>>;
    warnings:readonly string[];
}
export const EMPTY_SCRIPT_FORMAT:ScriptFormatSource={page:{},elements:{},warnings:[]};
const names:Record<string,string>={'scene-heading':'Scene Heading',action:'Action',character:'Character',
    dialogue:'Dialogue',parenthetical:'Parenthetical',transition:'Transition',shot:'Shot'};
const attrs=(node?:Element):Record<string,string>=>node?Object.fromEntries([...node.attributes].map(a=>[a.name,a.value])):{};
const children=(node:Element,name:string)=>[...node.children].filter(child=>child.tagName===name);

/** Read only the preserved document envelope. Never synthesize local overrides
 * for absent values. Native references: format-resolution-native-findings.txt. */
export function readFDXFormatSource(xml?:string):ScriptFormatSource {
    if(!xml)return EMPTY_SCRIPT_FORMAT;
    const invalid={...EMPTY_SCRIPT_FORMAT,warnings:['Imported page settings could not be read; Story defaults are used.']};
    if(xml.length>5*1024*1024 || /<!DOCTYPE|<!ENTITY/i.test(xml))return invalid;
    const doc=new DOMParser().parseFromString(xml,'application/xml'),root=doc.documentElement;
    if(doc.querySelector('parsererror') || root.tagName!=='FinalDraft' || root.namespaceURI)return invalid;
    const warnings=new Set<string>();
    const layouts=children(root,'PageLayout'),page=attrs(layouts[0]);
    if(layouts.length>1)return invalid;
    const sizeNode=layouts[0]&&children(layouts[0],'PageSize')[0],size=attrs(sizeNode);
    if(sizeNode && (Number(size.Width)!==8.5 || Number(size.Height)!==11))warnings.add('This preview uses US Letter; the imported paper size is retained in FDX.');
    if(page.DocumentLeading && page.DocumentLeading!=='Normal')warnings.add('Imported document line spacing is approximated with 12-point lines.');
    const elements:Record<string,Readonly<Record<string,string>>>=Object.create(null);
    const paginateAs:Record<string,string>=Object.create(null);
    const fonts:Record<string,Readonly<Record<string,string>>>=Object.create(null);
    for(const node of children(root,'ElementSettings')){
        const name=node.getAttribute('Type');if(!name)continue;
        if(Object.hasOwn(elements,name)){warnings.add('Duplicate imported element settings were ignored.');continue;}
        elements[name]=attrs(children(node,'ParagraphSpec')[0]);
        const pagination=children(node,'Behavior')[0]?.getAttribute('PaginateAs');
        if(pagination)paginateAs[name]=pagination;
        const font=attrs(children(node,'FontSpec')[0]);
        fonts[name]=font;
    }
    warnings.add('Exact Final Draft page fitting, continuation settings and font metrics are still approximated.');
    return {page,elements,fonts,paginateAs,warnings:[...warnings]};
}

export function scriptElementFont(type:string,source:ScriptFormatSource):Readonly<Record<string,string>> {
    return source.fonts?.[names[type]]??{};
}

export function scriptSentenceBreaksEnabled(source:ScriptFormatSource):boolean {
    // Native XML reader008d66f0 defaults missing/empty to enabled.
    return source.page.BreakDialogueAndActionAtSentences!=='No';
}
export function usesSentenceBreaks(type:ScriptBlockData['type'],source:ScriptFormatSource):boolean {
    const own=names[type],override=source.paginateAs?.[own];
    const category=override && ['General',...Object.values(names)].includes(override)?override:own;
    return ['Action','Dialogue','Parenthetical'].includes(category);
}

/** Bare FDX indents are inches: native 008d7970/008d7ad0/008d7790 ->
 * 00a44be0 (*72) -> 00a31b90 (half ties toward positive infinity).
 * Other unit suffixes are preserved but outside this supported input subset. */
function numeric(value:string|undefined):number|undefined {
    if(value===undefined || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value.trim()))return;
    const number=Number(value);return Number.isFinite(number)?number:undefined;
}
export function resolveScriptPage(source:ScriptFormatSource):{top:number;bottom:number;rows:number;warnings:string[]} {
    const warnings:string[]=[];
    const margin=(name:string)=>{
        const raw=source.page[name];if(raw===undefined || raw==='')return 72;
        const value=numeric(raw);
        if(value===undefined || value<0 || value>600){warnings.push('An invalid imported page margin was replaced with the Story default.');return 72;}
        return value;
    };
    const top=margin('TopMargin'),bottom=margin('BottomMargin'),rows=Math.floor((792-top-bottom)/12);
    if(rows<8)return {top:72,bottom:72,rows:54,warnings:['Imported margins leave too little writing space; Story defaults are used.']};
    return {top,bottom,rows,warnings};
}

export function resolveParagraphFormat(block:Pick<ScriptBlockData,'type'|'richText'>,source:ScriptFormatSource,
    fallback:ParagraphGeometry):ResolvedParagraphFormat {
    const warnings:string[]=[],local=block.richText?.paragraphAttributes??{};
    // Current editor role wins over stale Type metadata after a role change.
    const element=source.elements[names[block.type]]??{};
    const raw=(key:string)=>local[key]!==undefined && local[key]!==''?local[key]:element[key];
    const points=(key:string,defaultValue:number)=>{
        const value=raw(key);if(value===undefined || value==='')return defaultValue;
        const parsed=numeric(value);
        if(parsed===undefined){warnings.push('An unsupported paragraph measurement uses the Story default.');return defaultValue;}
        return Math.round(parsed*72);
    };
    let x=points('LeftIndent',fallback.x),right=points('RightIndent',fallback.x+fallback.width),firstIndent=points('FirstIndent',0);
    if(![x,right,firstIndent].every(Number.isFinite) || x<0 || right>612 || right-x<7.2 || x+firstIndent<0 || right-x-firstIndent<7.2){
        warnings.push('An unsupported paragraph indent uses the Story default.');x=fallback.x;right=x+fallback.width;firstIndent=0;
    }
    const space=raw('SpaceBefore'),spacing=numeric(space);
    let before=fallback.before;
    if(space!==undefined && space!==''){
        if(spacing===undefined || spacing<0 || spacing>600)warnings.push('An invalid paragraph gap uses the Story default.');
        else {before=Math.round(spacing/12);if(spacing%12!==0)warnings.push('Paragraph spacing is rounded to 12-point lines.');}
    }
    const alignment=raw('Alignment');let align=fallback.align;
    if(alignment && ['Left','Right','Center'].includes(alignment))align=alignment.toLowerCase() as ParagraphGeometry['align'];
    else if(alignment)warnings.push('An unsupported paragraph alignment uses the Story default.');
    // FD13.4 native 00da8d10 scales accumulated height using paragraph+23c,
    // then truncates the fixed-point result (00da8ec5..00da8ed9).
    // Resolve the multiplier in12pt base units; layout applies each line's
    // Courier size. Application leading adjustments in00a2df20 remain separate.
    const spacingValue=numeric(raw('Spacing'));
    const multiplier=spacingValue!==undefined && [1,1.5,2].includes(spacingValue)?spacingValue:1;
    if(raw('Spacing') && spacingValue!==1 && spacingValue!==1.5 && spacingValue!==2)
        warnings.push('Unsupported paragraph line spacing uses single-spaced lines.');
    if(raw('Leading') && raw('Leading')!=='Regular')warnings.push('Imported paragraph leading is approximated with regular leading.');
    const starts=raw('StartsNewPage');
    if(starts && starts!=='Yes' && starts!=='No')warnings.push('An unsupported page-break value was ignored.');
    return {x,width:right-x,before,align,firstIndent,startsNewPage:starts==='Yes',lineHeight:Math.floor(12*multiplier),warnings};
}
